import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCourse, getModules, getLessons,
  createModule, updateModule, deleteModule,
  createLesson, updateLesson, deleteLesson
} from '../../utils/api';

// DnD
import {
  DndContext, closestCenter, PointerSensor,
  KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// UI
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import RichTextEditor from '../../components/RichTextEditor';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, ChevronDown, ChevronRight, Play,
  FileText, HelpCircle, GripVertical, Eye, ArrowLeft, Save
} from 'lucide-react';

// ── quiz builder ────────────────────────────────────────────────────────────────
const QuizBuilder = ({ questions, onChange }) => {
  const add = () => onChange([...questions, { question: '', options: ['', '', '', ''], correct_index: 0 }]);
  const remove = (qi) => onChange(questions.filter((_, i) => i !== qi));
  const updateQ = (qi, field, val) => { const q=[...questions]; q[qi]={...q[qi],[field]:val}; onChange(q); };
  const updateOpt = (qi, oi, val) => { const q=[...questions]; q[qi].options[oi]=val; onChange(q); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Preguntas del Quiz</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="add-question-btn">
          <Plus className="w-3 h-3 mr-1" />Agregar pregunta
        </Button>
      </div>
      {questions.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3 border rounded-lg">Sin preguntas aún</p>
      )}
      {questions.map((q, qi) => (
        <div key={qi} className="p-4 border rounded-lg bg-muted/20 space-y-3" data-testid={`question-builder-${qi}`}>
          <div className="flex items-start gap-2">
            <span className="mt-2 text-xs font-bold text-muted-foreground w-5 shrink-0">{qi + 1}.</span>
            <Input placeholder="Escribe la pregunta..." value={q.question} onChange={e => updateQ(qi,'question',e.target.value)} className="flex-1" data-testid={`question-text-${qi}`} />
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(qi)} className="shrink-0 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4"/></Button>
          </div>
          <div className="ml-7 space-y-2">
            <p className="text-xs text-muted-foreground">Opciones (selecciona la correcta con el radio):</p>
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type="radio" name={`correct-${qi}`} checked={q.correct_index===oi} onChange={() => updateQ(qi,'correct_index',oi)} className="accent-primary shrink-0" data-testid={`correct-radio-${qi}-${oi}`} />
                <Input placeholder={`Opción ${String.fromCharCode(65+oi)}`} value={opt} onChange={e => updateOpt(qi,oi,e.target.value)} className="h-8 text-sm" data-testid={`option-input-${qi}-${oi}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── lesson dialog ───────────────────────────────────────────────────────────────
const DEFAULTS = { title:'', type:'video', content:'', duration_minutes:'', order:1, is_preview:false, quiz_questions:[] };

const LessonDialog = ({ open, onClose, onSave, lesson, nextOrder }) => {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(lesson
        ? { ...DEFAULTS, ...lesson, duration_minutes: lesson.duration_minutes?.toString()||'', quiz_questions: lesson.quiz_questions||[] }
        : { ...DEFAULTS, order: nextOrder }
      );
    }
  }, [open, lesson, nextOrder]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('El título es requerido'); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        order: parseInt(form.order) || nextOrder,
        quiz_questions: form.type==='quiz' ? form.quiz_questions : [],
        content: form.type==='quiz' ? null : form.content,
      });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{lesson ? 'Editar Lección' : 'Nueva Lección'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Título *</Label>
              <Input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Nombre de la lección" data-testid="lesson-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Lección</Label>
              <Select value={form.type} onValueChange={v=>setForm(p=>({...p,type:v}))}>
                <SelectTrigger data-testid="lesson-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duración (min)</Label>
              <Input type="number" min="1" value={form.duration_minutes} onChange={e=>setForm(p=>({...p,duration_minutes:e.target.value}))} placeholder="ej: 15" />
            </div>
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input type="number" min="1" value={form.order} onChange={e=>setForm(p=>({...p,order:e.target.value}))} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="is_preview" checked={form.is_preview} onChange={e=>setForm(p=>({...p,is_preview:e.target.checked}))} className="accent-primary" />
              <Label htmlFor="is_preview" className="cursor-pointer text-sm">Vista previa gratuita</Label>
            </div>
          </div>

          {form.type === 'video' && (
            <div className="space-y-2">
              <Label>URL del Video</Label>
              <Input value={form.content||''} onChange={e=>setForm(p=>({...p,content:e.target.value}))} placeholder="https://youtube.com/watch?v=..." data-testid="lesson-content-input" />
              <p className="text-xs text-muted-foreground">YouTube, Vimeo o URL de video directo</p>
            </div>
          )}

          {form.type === 'text' && (
            <div className="space-y-2">
              <Label>Contenido</Label>
              <RichTextEditor
                key={`editor-${lesson?.id || 'new'}-${open}`}
                value={form.content || ''}
                onChange={v => setForm(p => ({ ...p, content: v }))}
                placeholder="Escribe el contenido de la lección..."
                minHeight={220}
              />
            </div>
          )}

          {form.type === 'quiz' && (
            <QuizBuilder
              questions={form.quiz_questions}
              onChange={qs => setForm(p => ({ ...p, quiz_questions: qs }))}
            />
          )}

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-lesson-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : lesson ? 'Actualizar' : 'Crear Lección'}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── sortable lesson item ────────────────────────────────────────────────────────
const TYPE_ICONS = {
  video: <Play className="w-3.5 h-3.5 text-blue-500" />,
  text: <FileText className="w-3.5 h-3.5 text-green-500" />,
  quiz: <HelpCircle className="w-3.5 h-3.5 text-amber-500" />,
};

const SortableLessonItem = ({ lesson, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2.5 rounded-lg border bg-background transition-colors ${isDragging ? 'shadow-lg border-primary/50' : 'hover:border-primary/30'}`}
      data-testid={`lesson-item-${lesson.id}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground/40 hover:text-muted-foreground shrink-0" title="Arrastrar para reordenar">
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      {TYPE_ICONS[lesson.type] || TYPE_ICONS.text}
      <span className="flex-1 text-sm truncate">{lesson.title}</span>
      {lesson.duration_minutes && <span className="text-xs text-muted-foreground shrink-0">{lesson.duration_minutes}m</span>}
      {lesson.is_preview && <Badge variant="outline" className="text-xs shrink-0">Gratis</Badge>}
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => onEdit(lesson)} data-testid={`edit-lesson-${lesson.id}`}><Edit className="w-3.5 h-3.5" /></Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0 text-destructive hover:text-destructive" onClick={() => onDelete(lesson.id)} data-testid={`delete-lesson-${lesson.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
    </div>
  );
};

// ── module section (with sortable lessons inside) ───────────────────────────────
const ModuleSection = ({ courseId, module, dragHandleProps, onModuleUpdate, onModuleDelete, onLessonSave, onLessonDelete, onLessonsReorder }) => {
  const [expanded, setExpanded] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(module.title);
  const [lessonDialog, setLessonDialog] = useState({ open: false, lesson: null });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleLessonDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const lessons = module.lessons || [];
    const oldIdx = lessons.findIndex(l => l.id === active.id);
    const newIdx = lessons.findIndex(l => l.id === over.id);
    const reordered = arrayMove(lessons, oldIdx, newIdx);
    onLessonsReorder(module.id, reordered);
    // persist orders
    await Promise.all(reordered.map((l, i) => updateLesson(courseId, module.id, l.id, { order: i + 1 }).catch(() => {})));
  };

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) return;
    await onModuleUpdate(module.id, { title: titleValue });
    setEditingTitle(false);
  };

  return (
    <Card className="mb-4" data-testid={`module-section-${module.id}`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setExpanded(e => !e)} className="p-1 hover:bg-muted rounded shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button {...dragHandleProps} className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground/40 hover:text-muted-foreground shrink-0" title="Arrastrar módulo">
            <GripVertical className="w-4 h-4" />
          </button>
          {editingTitle ? (
            <Input value={titleValue} onChange={e => setTitleValue(e.target.value)} onBlur={handleSaveTitle} onKeyDown={e => e.key === 'Enter' && handleSaveTitle()} className="h-7 text-sm font-semibold flex-1" autoFocus />
          ) : (
            <span className="font-semibold text-sm flex-1 cursor-pointer hover:text-primary" onClick={() => setEditingTitle(true)} title="Clic para editar">{module.title}</span>
          )}
          <Badge variant="secondary" className="text-xs shrink-0">{(module.lessons||[]).length} lec.</Badge>
          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive h-7 w-7 p-0 shrink-0" onClick={() => onModuleDelete(module.id)} data-testid={`delete-module-${module.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-3 px-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleLessonDragEnd}>
            <SortableContext items={(module.lessons||[]).map(l=>l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5 mb-3">
                {(module.lessons||[]).length === 0 && (
                  <p className="text-xs text-muted-foreground py-2 text-center">Sin lecciones aún</p>
                )}
                {(module.lessons||[]).map(lesson => (
                  <SortableLessonItem
                    key={lesson.id}
                    lesson={lesson}
                    onEdit={l => setLessonDialog({ open: true, lesson: l })}
                    onDelete={id => onLessonDelete(module.id, id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <Button size="sm" variant="outline" className="w-full text-xs gap-1" onClick={() => setLessonDialog({ open: true, lesson: null })} data-testid={`add-lesson-btn-${module.id}`}>
            <Plus className="w-3.5 h-3.5" />Agregar Lección
          </Button>
        </CardContent>
      )}

      <LessonDialog
        open={lessonDialog.open}
        onClose={() => setLessonDialog({ open: false, lesson: null })}
        lesson={lessonDialog.lesson}
        nextOrder={(module.lessons?.length||0)+1}
        onSave={data => onLessonSave(module.id, lessonDialog.lesson?.id, data)}
      />
    </Card>
  );
};

// sortable wrapper for module
const SortableModuleSection = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.module.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'shadow-xl z-10 relative' : ''}>
      <ModuleSection {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

// ── main ─────────────────────────────────────────────────────────────────────────
const EditCourseContent = () => {
  const { courseId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModuleTitle, setAddModuleTitle] = useState('');
  const [addingModule, setAddingModule] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['superadmin', 'admin', 'professor'].includes(user.role)) { navigate('/dashboard'); return; }
    loadAll();
  }, [courseId, user, authLoading]);

  const loadAll = useCallback(async () => {
    try {
      const [courseRes, modulesRes] = await Promise.all([getCourse(courseId), getModules(courseId)]);
      const courseData = courseRes.data;
      if (user.role === 'professor' && courseData.instructor_id !== user.id) {
        toast.error('No tienes acceso a este curso'); navigate('/dashboard'); return;
      }
      setCourse(courseData);
      const modsWithLessons = await Promise.all(
        modulesRes.data.map(async mod => {
          const lr = await getLessons(courseId, mod.id);
          return { ...mod, lessons: lr.data };
        })
      );
      setModules(modsWithLessons);
    } catch { toast.error('Error al cargar el contenido del curso'); }
    finally { setLoading(false); }
  }, [courseId, user, navigate]);

  const handleModuleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = modules.findIndex(m => m.id === active.id);
    const newIdx = modules.findIndex(m => m.id === over.id);
    const reordered = arrayMove(modules, oldIdx, newIdx);
    setModules(reordered);
    await Promise.all(reordered.map((m, i) => updateModule(courseId, m.id, { order: i + 1 }).catch(() => {})));
    toast.success('Módulos reordenados');
  };

  const handleModuleUpdate = async (moduleId, data) => {
    try { await updateModule(courseId, moduleId, data); setModules(prev => prev.map(m => m.id === moduleId ? { ...m, ...data } : m)); }
    catch { toast.error('Error al actualizar módulo'); }
  };

  const handleModuleDelete = async (moduleId) => {
    if (!window.confirm('¿Eliminar este módulo y todas sus lecciones?')) return;
    try { await deleteModule(courseId, moduleId); setModules(prev => prev.filter(m => m.id !== moduleId)); toast.success('Módulo eliminado'); }
    catch { toast.error('Error al eliminar módulo'); }
  };

  const handleLessonSave = async (moduleId, lessonId, data) => {
    try {
      if (lessonId) {
        const res = await updateLesson(courseId, moduleId, lessonId, data);
        setModules(prev => prev.map(m => m.id === moduleId ? { ...m, lessons: m.lessons.map(l => l.id === lessonId ? res.data : l) } : m));
        toast.success('Lección actualizada');
      } else {
        const res = await createLesson(courseId, moduleId, { ...data, module_id: moduleId });
        setModules(prev => prev.map(m => m.id === moduleId ? { ...m, lessons: [...m.lessons, res.data] } : m));
        toast.success('Lección creada');
      }
    } catch { toast.error('Error al guardar la lección'); }
  };

  const handleLessonDelete = async (moduleId, lessonId) => {
    if (!window.confirm('¿Eliminar esta lección?')) return;
    try {
      await deleteLesson(courseId, moduleId, lessonId);
      setModules(prev => prev.map(m => m.id === moduleId ? { ...m, lessons: m.lessons.filter(l => l.id !== lessonId) } : m));
      toast.success('Lección eliminada');
    } catch { toast.error('Error al eliminar la lección'); }
  };

  const handleLessonsReorder = (moduleId, newLessons) => {
    setModules(prev => prev.map(m => m.id === moduleId ? { ...m, lessons: newLessons } : m));
  };

  const handleAddModule = async () => {
    if (!addModuleTitle.trim()) { toast.error('El título del módulo es requerido'); return; }
    setAddingModule(true);
    try {
      const res = await createModule(courseId, { course_id: courseId, title: addModuleTitle, order: modules.length + 1 });
      setModules(prev => [...prev, { ...res.data, lessons: [] }]);
      setAddModuleTitle('');
      toast.success('Módulo creado');
    } catch { toast.error('Error al crear módulo'); }
    finally { setAddingModule(false); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando contenido...</div>;

  const totalLessons = modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);

  return (
    <div className="min-h-screen bg-secondary" data-testid="edit-course-content-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Link to={user?.role === 'professor' ? '/dashboard' : '/admin/courses'}>
              <Button variant="ghost" size="sm" className="gap-1 mb-3 -ml-2 text-muted-foreground">
                <ArrowLeft className="w-4 h-4" />{user?.role === 'professor' ? 'Mi panel' : 'Gestionar Cursos'}
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">{course?.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={course?.status === 'published' ? 'default' : 'secondary'}>
                {course?.status === 'published' ? 'Publicado' : course?.status === 'draft' ? 'Borrador' : 'Archivado'}
              </Badge>
              <span className="text-sm text-muted-foreground">{modules.length} módulos · {totalLessons} lecciones</span>
              <span className="text-xs text-muted-foreground italic">· Arrastra para reordenar</span>
            </div>
          </div>
          <Link to={`/courses/${courseId}/learn`}>
            <Button variant="outline" className="gap-2" data-testid="preview-course-btn">
              <Eye className="w-4 h-4" />Vista previa
            </Button>
          </Link>
        </div>

        {/* Sortable modules */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
          <SortableContext items={modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
            {modules.map(mod => (
              <SortableModuleSection
                key={mod.id}
                module={mod}
                courseId={courseId}
                onModuleUpdate={handleModuleUpdate}
                onModuleDelete={handleModuleDelete}
                onLessonSave={handleLessonSave}
                onLessonDelete={handleLessonDelete}
                onLessonsReorder={handleLessonsReorder}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add module */}
        <Card className="border-dashed border-2 border-muted-foreground/20 bg-muted/10">
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2 text-muted-foreground">Agregar nuevo módulo</p>
            <div className="flex gap-2">
              <Input
                placeholder="Título del módulo..."
                value={addModuleTitle}
                onChange={e => setAddModuleTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddModule()}
                className="flex-1"
                data-testid="add-module-input"
              />
              <Button onClick={handleAddModule} disabled={addingModule || !addModuleTitle.trim()} data-testid="add-module-btn">
                <Plus className="w-4 h-4 mr-2" />{addingModule ? 'Creando...' : 'Agregar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {modules.length === 0 && (
          <p className="text-center py-6 text-sm text-muted-foreground">Este curso no tiene módulos. Agrega el primero arriba.</p>
        )}
      </div>
    </div>
  );
};

export default EditCourseContent;
